import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Pagination, Popover, Alert, Collapse } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  PAGE_PORTAL_INVENTORY_PRODUCT_DETAIL,
  PORTAL_PREFIX,
  KivuI18nContext,
  useKivunovaTranslation,
  localizedPath
} from "@kivunova/kivufrontendcommon";

import { RootState } from "../../store/store";
import { DEFAULT_PAGE, DEFAULT_SIZE } from "./filters/types";
import {
  useGetProductList,
  useGetLowStockAlerts
} from "../../hooks/api/useProduct";
import type { Product } from "./types/products";
import NoProductListFound from "./NoProductListFound";
import AddProductModal from "./AddProductModal";
import NoCategoryForProductDialog from "./NoCategoryForProductDialog";
import { useTryOpenAddProduct } from "./hooks/useTryOpenAddProduct";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import ExportData from "../common/ExportData/ExportData";
import { exportProductColumns } from "../common/constants/exportColumns";
import { ProductListFilters } from "./filters";
import { setProductListRequest } from "../../store/slices/productSlice";
import ProductListToolbar from "./ProductListToolbar";
import { ProductListTable } from "./ProductListTable";
import { sortProducts } from "./utils/productListUtils";
import { isUserCompanyAdmin } from "../../hooks/api/useProductItem";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const ProductList: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyAdminProductStoreByCompany = useSelector(
    (state: RootState) => state.products.companyAdminProductListStoreIdByCompany
  );
  const {
    productList,
    productListRequest,
    productListRefreshTrigger,
    productListTotalElements: productListTotalCount,
    productListCurrentPage: productListPage,
    productListPageSize: productListSize,
    lowStockAlerts
  } = useSelector((state: RootState) => state.products);
  const mutation = useGetProductList();
  const lowStockAlertsMutation = useGetLowStockAlerts();
  const mutateRef = useRef(mutation.mutateAsync);
  mutateRef.current = mutation.mutateAsync;

  const {
    addProductOpen,
    noCategoryOpen,
    checkingCategories,
    tryOpenAddProduct,
    closeAddProduct,
    closeNoCategory
  } = useTryOpenAddProduct();

  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const filtersOpen = Boolean(filtersAnchorEl);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof Product>("name");

  const companyAdminListStoreId = useMemo(() => {
    if (!isUserCompanyAdmin(userDetails) || !userDetails?.companyId?.trim()) {
      return undefined;
    }
    const id =
      companyAdminProductStoreByCompany[userDetails.companyId.trim()]?.trim();
    return id && id.length > 0 ? id : undefined;
  }, [userDetails, companyAdminProductStoreByCompany]);

  const requestKey = useMemo(
    () =>
      `${JSON.stringify(productListRequest ?? {})}:${productListRefreshTrigger}:${companyAdminListStoreId ?? ""}`,
    [productListRequest, productListRefreshTrigger, companyAdminListStoreId]
  );

  const productListRequestRef = useRef(productListRequest);
  productListRequestRef.current = productListRequest;

  const isFirstCatalogStoreEffect = useRef(true);
  useEffect(() => {
    if (isFirstCatalogStoreEffect.current) {
      isFirstCatalogStoreEffect.current = false;
      return;
    }
    if (!isUserCompanyAdmin(userDetails)) return;
    const plr = productListRequestRef.current;
    dispatch(
      setProductListRequest({
        ...plr,
        page: DEFAULT_PAGE,
        size: plr?.size ?? productListSize
      })
    );
  }, [companyAdminListStoreId, dispatch, productListSize, userDetails]);

  useEffect(() => {
    const run = async () => {
      try {
        const req = {
          page: productListRequest?.page ?? DEFAULT_PAGE,
          size: productListRequest?.size ?? DEFAULT_SIZE,
          ...productListRequest,
          ...(companyAdminListStoreId
            ? { storeId: companyAdminListStoreId }
            : {})
        };
        //structured log with module, operation, user context
        sendAppLog(
          LOG_LEVEL.INFO,
          "Fetching product list",
          buildLogContext(
            "products",
            "list-products-ui",
            userDetails?.id,
            userDetails?.companyId,
            req.storeId,
            { page: req.page, size: req.size }
          )
        );
        await mutateRef.current(req);
      } catch (error) {
        const req = {
          page: productListRequest?.page ?? DEFAULT_PAGE,
          size: productListRequest?.size ?? DEFAULT_SIZE,
          ...productListRequest,
          ...(companyAdminListStoreId
            ? { storeId: companyAdminListStoreId }
            : {})
        };
        captureAppException(
          "Failed to fetch product list",
          error as Error,
          buildLogContext(
            "products",
            "list-products-ui",
            userDetails?.id,
            userDetails?.companyId,
            req.storeId
          )
        );
      }
    };
    run();
  }, [
    requestKey,
    userDetails,
    companyAdminListStoreId,
    productListRequest,
    productListSize
  ]);

  // Fetch low stock alerts on component mount
  useEffect(() => {
    lowStockAlertsMutation.mutate();
  }, []);

  const handleCloseFilters = useCallback(() => {
    setFiltersAnchorEl(null);
  }, []);

  const sortedList = useMemo(
    () => sortProducts(productList ?? [], orderBy, order),
    [productList, order, orderBy]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedIds(sortedList.map((p) => p.id));
      } else {
        setSelectedIds([]);
      }
    },
    [sortedList]
  );

  const handleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  }, []);

  const handleSort = useCallback(
    (property: keyof Product) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      // MUI Pagination uses 1-based page index; API uses 0-based
      const apiPage = page - 1;
      dispatch(
        setProductListRequest({
          ...productListRequest,
          page: apiPage,
          size: productListRequest?.size ?? productListSize
        })
      );
    },
    [dispatch, productListRequest, productListSize]
  );

  const handleViewProduct = useCallback(
    (productId: string) => {
      const route = PAGE_PORTAL_INVENTORY_PRODUCT_DETAIL.replace(
        ":productId",
        productId
      );
      navigate(
        localizedPath(`${PORTAL_PREFIX}/${route}`, language, defaultLang)
      );
    },
    [navigate, language, defaultLang]
  );

  const handleExportComplete = useCallback(
    (format: string) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Product list exported",
        buildLogContext(
          "products",
          "export-product-list",
          userDetails?.id,
          userDetails?.companyId,
          userDetails?.storeId,
          { format, count: sortedList.length }
        )
      );
    },
    [sortedList.length, userDetails]
  );

  const handleExportError = useCallback(
    (error: Error) => {
      captureAppException(
        "Product list export failed",
        error,
        buildLogContext(
          "products",
          "export-product-list",
          userDetails?.id,
          userDetails?.companyId,
          userDetails?.storeId
        )
      );
    },
    [userDetails]
  );

  if (mutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-products", "Loading products...")}
      />
    );
  }

  const isEmpty =
    (!productList || productList.length === 0) && !mutation.isPending;

  const totalPages =
    productListTotalCount > 0
      ? Math.ceil(productListTotalCount / productListSize)
      : 1;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        minHeight: "calc(100vh - 110px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: "100%",
        padding: 0,
        px: matchMD ? 1 : 0,
        pt: 2,
        color: "text.primary",
        mt: 1
      }}
    >
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        gap={4}
        flexDirection="column"
        display="flex"
      >
        <Box component={motion.div} variants={itemVariants}>
          <ProductListToolbar
            onOpenFilters={(e) => setFiltersAnchorEl(e.currentTarget)}
            onAddProduct={() => tryOpenAddProduct()}
            addProductDisabled={checkingCategories}
            isMobile={matchMD}
          >
            {!isEmpty && (
              <ExportData
                data={sortedList}
                columns={exportProductColumns}
                filename={`products_${new Date().toISOString().split("T")[0]}`}
                title={t("export-product-list", "Export Product List")}
                buttonVariant="outlined"
                buttonText={t("export", "Export")}
                formats={["csv", "xlsx", "pdf", "json"]}
                onExportComplete={handleExportComplete}
                onExportError={handleExportError}
              />
            )}
          </ProductListToolbar>
        </Box>

        {/* Low Stock Alerts */}
        <Collapse in={lowStockAlerts.length > 0}>
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Box
                component="button"
                onClick={() => lowStockAlertsMutation.mutate()}
                sx={{
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "inherit",
                  fontSize: "14px",
                  textDecoration: "underline"
                }}
              >
                {t("refresh", "Refresh")}
              </Box>
            }
          >
            {t(
              "low-stock-alerts-message",
              `Low stock alerts: ${lowStockAlerts.length} product(s) need attention`,
              { count: lowStockAlerts.length }
            )}
          </Alert>
        </Collapse>

        <Popover
          open={filtersOpen}
          anchorEl={filtersAnchorEl}
          onClose={handleCloseFilters}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: matchMD ? "90vw" : 360,
                maxWidth: matchMD ? "95vw" : 640,
                maxHeight: "85vh",
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: "none"
              }
            }
          }}
        >
          <ProductListFilters
            onApplySuccess={handleCloseFilters}
            onClearSuccess={handleCloseFilters}
          />
        </Popover>

        <AnimatePresence mode="wait">
          {isEmpty ? (
            <Box component={motion.div} variants={itemVariants} key="empty">
              <NoProductListFound />
            </Box>
          ) : (
            <Box
              component={motion.div}
              variants={containerVariants}
              key="content"
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Box component={motion.div} variants={itemVariants}>
                <ProductListTable
                  products={sortedList}
                  selectedIds={selectedIds}
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                  onSelectAll={handleSelectAll}
                  onSelectOne={handleSelectOne}
                  onViewProduct={handleViewProduct}
                  isMobile={matchMD}
                />
              </Box>
              <Box
                component={motion.div}
                variants={itemVariants}
                display="flex"
                justifyContent="center"
                mt={1}
                sx={{ overflowX: "auto", pb: 1 }}
              >
                <Pagination
                  variant="outlined"
                  count={totalPages}
                  page={productListPage + 1}
                  onChange={handlePageChange}
                  shape="rounded"
                  color="primary"
                  size={matchMD ? "small" : "medium"}
                  siblingCount={matchMD ? 0 : 1}
                  boundaryCount={matchMD ? 1 : 2}
                />
              </Box>
            </Box>
          )}
        </AnimatePresence>

        <AddProductModal open={addProductOpen} onClose={closeAddProduct} />
        <NoCategoryForProductDialog
          open={noCategoryOpen}
          onClose={closeNoCategory}
        />
      </Box>
    </Box>
  );
};

export default ProductList;
